import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/shared/auth/AuthContext';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Card, CardContent } from '@/shared/ui/card';
import { Zap, Eye, EyeOff } from 'lucide-react';
// La marque vient du DOMAINE d'arrivee, pas d'une variable de build : la meme image
// sert CFTSign et Flexcore. L'ecran affichait jusqu'ici le logo de l'un avec le nom
// de l'autre, en dur.
import { IDENTITE } from '@/shared/marque';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') setError(detail);
      else if (Array.isArray(detail)) setError(detail.map(d => d.msg || JSON.stringify(d)).join(' '));
      else setError('Erreur de connexion');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white p-4" data-testid="login-page">
      <div className="w-full max-w-sm animate-fade-in">
        <div className="text-center mb-10">
          <img src={IDENTITE.logo} alt={IDENTITE.nom} className="w-12 h-12 rounded-xl mx-auto mb-4" />
          <h1 className="text-xl font-semibold tracking-tight text-neutral-900">
            {IDENTITE.nom}
          </h1>
          <p className="text-sm text-neutral-500 mt-1">Connexion</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-3 rounded-md bg-red-50 border border-red-100 text-red-600 text-sm" data-testid="login-error">
              {error}
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="email" className="text-[13px] text-neutral-600">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Votre email"
              required
              className="h-10"
              data-testid="login-email-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password" className="text-[13px] text-neutral-600">Mot de passe</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPw ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Votre mot de passe"
                required
                className="h-10 pr-10"
                data-testid="login-password-input"
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                onClick={() => setShowPw(!showPw)}
              >
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <Button type="submit" className="w-full h-10" disabled={loading} data-testid="login-submit-btn">
            {loading ? 'Connexion...' : 'Se connecter'}
          </Button>
          <p className="text-center">
            <Link to="/forgot-password" className="text-[13px] text-neutral-500 hover:text-neutral-800 hover:underline"
                  data-testid="forgot-password-link">
              Mot de passe oublié ?
            </Link>
          </p>
        </form>

        {/* La signature de bas de page suit le domaine, comme l'en-tete au-dessus.
            Elle disait « FlexCore » en dur, y compris sur cftsign.fr : le poseur lisait
            le nom d'un editeur qu'il ne connait pas, a l'ecran ou il entre son mot de
            passe. */}
        <p className="text-center text-[11px] text-neutral-300 mt-8">
          {IDENTITE.nom}
        </p>
      </div>
    </div>
  );
}
